import { createReducer, on } from '@ngrx/store';

import {
  ChargerActivityResponseStoreModel,
  ChargingTimelineResponseStoreModel,
  EvseActivityResponseStoreModel,
} from '@domain/charger-details/charger-details.models';

import {
  chargerActivitySocketMessageArrive,
  chargerActivityTimelineSocketMessageArrive,
  clearSingleSiteChargerDetailsData,
  evseActivitySocketMessageArrive,
  evseActivityTimelineSocketMessageArrive,
  loadChargerActivity,
  loadChargerActivityFailure,
  loadChargerActivitySuccess,
  loadChargerActivityTimeline,
  loadChargerActivityTimelineFailure,
  loadChargerActivityTimelineSuccess,
  loadEvseActivity,
  loadEvseActivityFailure,
  loadEvseActivitySuccess,
  loadEvseActivityTimeline,
  loadEvseActivityTimelineFailure,
  loadEvseActivityTimelineSuccess,
} from './single-site-charger-details.actions';

export interface SingleSiteChargerDetailsState {
  chargerActivity?: ChargerActivityResponseStoreModel;
  chargerActivityIsLoading?: boolean;
  chargerActivityError?: string;

  chargerActivityTimeline?: ChargingTimelineResponseStoreModel;
  chargerActivityTimelineIsLoading?: boolean;
  chargerActivityTimelineError?: string;

  evseStates?: {
    [evseId: string]: EvseState;
  };
}

export interface EvseState {
  activity: {
    isLoading?: boolean;
    error?: string;
    activity?: EvseActivityResponseStoreModel;
  };

  activityTimeline?: ChargingTimelineResponseStoreModel;
  activityTimelineIsLoading?: boolean;
  activityTimelineError?: string;
}

export const initialState: SingleSiteChargerDetailsState = {};

export const singleSiteChargerDetailsFeatureKey = 'singleSiteChargerDetails';

export const singleSiteChargerDetailsReducer = createReducer(
  initialState,

  on(loadChargerActivity, (state) => ({ ...state, chargerActivityIsLoading: true, chargerActivityError: undefined })),
  on(loadChargerActivitySuccess, (state, { chargerActivity }) => ({
    ...state,
    chargerActivityIsLoading: false,
    chargerActivity,
  })),
  on(loadChargerActivityFailure, (state, { error }) => ({
    ...state,
    chargerActivityIsLoading: false,
    chargerActivityError: error,
  })),
  on(chargerActivitySocketMessageArrive, (state, { message }): SingleSiteChargerDetailsState => {
    return {
      ...state,
      chargerActivity: state.chargerActivity && {
        ...state.chargerActivity,
        activePowerKw: message.activePowerKw,
        csmsProxyLatencyMs: message.csmsProxyLatencyMs,
        chargerOperationalState: message.chargerOperationalState,
        alerts: message.alerts,
        powerGraphData: message.powerGraphData,
      },
    };
  }),

  on(loadEvseActivity, (state, { evseId }) => {
    return patchEvseState(state, evseId, (evseState) => {
      return {
        ...evseState,
        activity: {
          ...evseState.activity,
          isLoading: true,
          error: undefined,
        },
      };
    });
  }),
  on(loadEvseActivitySuccess, (state, { evseId, evseActivity }) => {
    return patchEvseState(state, evseId, (evseState) => {
      return {
        ...evseState,
        activity: {
          ...evseState.activity,
          isLoading: false,
          activity: evseActivity,
        },
      };
    });
  }),
  on(loadEvseActivityFailure, (state, { evseId, error }) => {
    return patchEvseState(state, evseId, (evseState) => {
      return {
        ...evseState,
        activity: {
          ...evseState.activity,
          isLoading: false,
          error,
        },
      };
    });
  }),
  on(evseActivitySocketMessageArrive, (state, { message, evseId }): SingleSiteChargerDetailsState => {
    return patchEvseState(state, evseId, (evseState) => {
      return {
        ...evseState,
        activity: {
          ...evseState.activity,
          activity: evseState.activity.activity && {
            ...evseState.activity.activity,
            ...message,
          },
        },
      };
    });
  }),

  on(loadChargerActivityTimeline, (state, { beforeTime }) => {
    return {
      ...state,
      chargerActivityTimelineIsLoading: true,
      chargerActivityTimelineError: undefined,
      chargerActivityTimeline: beforeTime ? state.chargerActivityTimeline : undefined,
    };
  }),
  on(loadChargerActivityTimelineSuccess, (state, { chargerActivityTimeline }) => {
    return {
      ...state,
      chargerActivityTimelineIsLoading: false,
      chargerActivityTimeline: state.chargerActivityTimeline
        ? {
            ...chargerActivityTimeline,
            events: [...state.chargerActivityTimeline.events, ...chargerActivityTimeline.events],
          }
        : chargerActivityTimeline,
    };
  }),
  on(loadChargerActivityTimelineFailure, (state, { error }) => {
    return {
      ...state,
      chargerActivityTimelineIsLoading: false,
      chargerActivityTimelineError: error,
    };
  }),
  on(chargerActivityTimelineSocketMessageArrive, (state, { message }): SingleSiteChargerDetailsState => {
    return {
      ...state,
      chargerActivityTimeline: {
        ...state.chargerActivityTimeline!,
        events: [message.event, ...state.chargerActivityTimeline!.events],
      },
    };
  }),

  on(loadEvseActivityTimeline, (state, { evseId, beforeTime }) => {
    return patchEvseState(state, evseId, (evseState): EvseState => {
      return {
        ...evseState,
        activityTimelineIsLoading: true,
        activityTimelineError: undefined,
        activityTimeline: beforeTime ? evseState.activityTimeline : undefined,
      };
    });
  }),
  on(loadEvseActivityTimelineSuccess, (state, { evseId, evseActivityTimeline }) => {
    return patchEvseState(state, evseId, (evseState): EvseState => {
      return {
        ...evseState,
        activityTimelineIsLoading: false,
        activityTimeline: evseState.activityTimeline
          ? {
              ...evseActivityTimeline,
              events: [...evseState.activityTimeline.events, ...evseActivityTimeline.events],
            }
          : evseActivityTimeline,
      };
    });
  }),
  on(loadEvseActivityTimelineFailure, (state, { evseId, error }) => {
    return patchEvseState(state, evseId, (evseState): EvseState => {
      return {
        ...evseState,
        activityTimelineIsLoading: false,
        activityTimelineError: error,
      };
    });
  }),
  on(evseActivityTimelineSocketMessageArrive, (state, { evseId, message }): SingleSiteChargerDetailsState => {
    return patchEvseState(state, evseId, (evseState): EvseState => {
      return {
        ...evseState,
        activityTimeline: {
          ...evseState.activityTimeline!,
          events: [message.event, ...evseState.activityTimeline!.events],
        },
      };
    });
  }),

  on(
    clearSingleSiteChargerDetailsData,
    (): SingleSiteChargerDetailsState => ({
      ...initialState,
    })
  )
);

const patchEvseState = (
  state: SingleSiteChargerDetailsState,
  evseId: string,
  patchFn: (evseState: EvseState) => EvseState
): SingleSiteChargerDetailsState => {
  const evseStates = { ...state.evseStates };

  if (!evseStates[evseId]) {
    evseStates[evseId] = {
      activity: {},
    };
  }

  evseStates[evseId] = patchFn(evseStates[evseId]);

  return {
    ...state,
    evseStates,
  };
};
